import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2, X } from "lucide-react";

import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

export function useCanPurge() {
    const { me, activeMembership, hasPerm } = useAuth();
    return !!me?.user?.is_platform_owner || activeMembership?.role?.code === "institution_owner" || hasPerm("trash.manage");
}

function ConfirmShell({ title, testid, busy, onClose, onConfirm, children }: {
    title: string; testid: string; busy: boolean; onClose: () => void; onConfirm: () => void; children: React.ReactNode;
}) {
    const { t } = useTranslation();
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
            onClick={() => !busy && onClose()} data-testid={testid}>
            <div className="surface-card w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3">
                    <h3 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{title}</h3>
                    <button onClick={onClose} disabled={busy} style={{ color: "var(--text-muted)" }} data-testid={`${testid}-close`}><X size={18} /></button>
                </div>
                <div className="flex items-start gap-2 rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}
                    data-testid={`${testid}-warning`}>
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {t("trash.purge.irreversible")}
                </div>
                <div className="text-sm space-y-1" style={{ color: "var(--text)" }}>{children}</div>
                <div className="flex justify-end gap-3 pt-2">
                    <button type="button" className="px-4 py-2 rounded-md border text-sm" disabled={busy}
                        style={{ borderColor: "var(--border)", color: "var(--text)" }} onClick={onClose} data-testid={`${testid}-cancel`}>
                        {t("common.cancel")}
                    </button>
                    <button type="button" disabled={busy} onClick={onConfirm} data-testid={`${testid}-confirm`}
                        className="px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                        style={{ background: "hsl(var(--destructive))", color: "hsl(var(--destructive-foreground))" }}>
                        {busy && <Loader2 className="animate-spin" size={14} />} {t("trash.purge.confirm_btn")}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function PurgeRowDialog({ batchId, name, onClose, onDone }: { batchId: string; name: string; onClose: () => void; onDone: () => void }) {
    const { t } = useTranslation();
    const [deps, setDeps] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get(`/trash/${batchId}/dependents`).then(r => setDeps((r.data.items || []).length)).catch(() => setDeps(null));
    }, [batchId]);
    const confirm = async () => {
        setBusy(true);
        try {
            await api.post(`/trash/${batchId}/purge`);
            toast.success(t("trash.purge.done"));
            onClose(); onDone();
        } catch (e: any) {
            toast.error(`${t("trash.purge.failed")} ${formatApiError(e) || ""}`.trim());
        } finally { setBusy(false); }
    };
    return (
        <ConfirmShell title={t("trash.purge.confirm_title")} testid="trash-purge-dialog" busy={busy} onClose={onClose} onConfirm={confirm}>
            <div data-testid="trash-purge-dialog-name"><b>{name}</b></div>
            <div style={{ color: "var(--text-muted)" }} data-testid="trash-purge-dialog-deps">
                {deps === null ? "…" : t("trash.purge.dependents", { count: deps })}
            </div>
        </ConfirmShell>
    );
}

export function EmptyTrashButton({ scopeLabel, tenantId, count, onDone }: { scopeLabel: string; tenantId?: string; count: number; onDone: () => void }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const confirm = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/trash/empty", tenantId ? { tenant_id: tenantId } : {});
            const msg = t("trash.purge.bulk_result", { deleted: data.purged ?? 0, skipped: data.failed ?? 0 });
            if ((data.failed ?? 0) === 0) toast.success(msg); else toast.warning(msg);
            setOpen(false); onDone();
        } catch (e: any) {
            toast.error(`${t("trash.purge.failed")} ${formatApiError(e) || ""}`.trim());
        } finally { setBusy(false); }
    };
    return (
        <>
            <button type="button" onClick={() => setOpen(true)} disabled={count === 0} data-testid="trash-empty-btn"
                className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium border disabled:opacity-50 transition-colors"
                style={{ borderColor: "hsl(var(--destructive))", color: "hsl(var(--destructive))" }}>
                <Trash2 size={14} /> {t("trash.purge.empty_btn")}
            </button>
            {open && (
                <ConfirmShell title={t("trash.purge.empty_title")} testid="trash-empty-dialog" busy={busy} onClose={() => setOpen(false)} onConfirm={confirm}>
                    <div data-testid="trash-empty-dialog-scope">{t("trash.purge.empty_scope", { scope: scopeLabel })}</div>
                    <div style={{ color: "var(--text-muted)" }}>{t("trash.purge.empty_count", { count })}</div>
                </ConfirmShell>
            )}
        </>
    );
}
